/** key of the physicist-entered last calibration date in scanner.extra (client workstations-edit) */
export const LAST_CALIBRATION_DATE_KEY = "Last Calibration Date";

/** month is 1-12 */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

/**
 * Parses the scanner's "Last Calibration Date" (scanner.extra). The client's
 * date picker writes MM/DD/YYYY; ISO YYYY-MM-DD is accepted too. Undefined
 * when missing, blank or not a real date. Time-zone free on purpose (common/
 * has no dependencies): the server and client turn it into local midnight in
 * the tenant's time zone with moment-timezone.
 */
export function parseCalibrationDate(
  extra: { [key: string]: any } | null | undefined
): CalendarDate | undefined {
  const raw = extra ? extra[LAST_CALIBRATION_DATE_KEY] : undefined;
  if (typeof raw !== "string") return undefined;
  const value = raw.trim();
  let year: number;
  let month: number;
  let day: number;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (us) {
    month = Number(us[1]);
    day = Number(us[2]);
    year = Number(us[3]);
  } else if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else {
    return undefined;
  }
  // reject impossible dates (e.g. 02/30) instead of letting them roll over
  const check = new Date(Date.UTC(year, month - 1, day));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return undefined;
  }
  return { year, month, day };
}

/**
 * Light grey for measurements from before the scanner's last calibration on
 * the charts' dark background (#1a1a1a): still readable, clearly not part of
 * the current regime. Shared by the Trends page and the server-rendered
 * report trend images so both tell the same story.
 */
export const BEFORE_CALIBRATION_COLOR = "#c8c8c8";

/**
 * A measurement belongs to the current calibration regime when it was
 * scanned at or after local midnight of the scanner's "Last Calibration
 * Date" (the day of the calibration counts). Without a calibration date
 * everything is current.
 */
export function isSinceCalibration(
  scanDateMs: number,
  calibrationMs: number | undefined
): boolean {
  return calibrationMs === undefined || scanDateMs >= calibrationMs;
}

export interface CalibrationSplit<T> {
  /** the current regime: statistics, baselines, projections run on these */
  since: T[];
  /** display only: drawn in grey, never part of a statistic */
  before: T[];
}

/**
 * ONE place that decides which tests are before and which are since the
 * scanner's last calibration, by scan date. The Trends page, Download Data,
 * the report trend images, the outlier / drift statistics, the MG baseline
 * window and the "tests since calibration" panel all call this (the server
 * and client turn the calibration date into a local-midnight instant with
 * moment-timezone first). Order of the input is kept in both halves.
 */
export function splitByCalibration<T>(
  items: T[],
  scanDateMs: (item: T) => number,
  calibrationMs: number | undefined
): CalibrationSplit<T> {
  const since: T[] = [];
  const before: T[] = [];
  for (const item of items) {
    if (isSinceCalibration(scanDateMs(item), calibrationMs)) {
      since.push(item);
    } else {
      before.push(item);
    }
  }
  return { since, before };
}
